import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, Signal, signal, untracked } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { CreatorCustomValueCheckService } from '@core/services/creator-custom-value-check.service';
import { EPlatform } from '@enums/campaign.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ALL_CREATORS_ID, ALL_CREATORS_OPTION } from '@shared/constants/all-creators.constants';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { SignalSetUtilitiesService } from '@utilities/signal-set-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { uniqBy } from 'lodash';

@Component({
  imports: [
    MatButtonModule,
    TranslateModule,
    IconBadgeComponent,
    IconComponent,
    MatMenuModule,
    TextClampTooltipDirective,
    MatTooltipModule,
    NgClass,
  ],
  selector: 'app-creator-select-menu',
  styleUrls: ['./creator-select-menu.component.scss'],
  templateUrl: './creator-select-menu.component.html',
})
export class CreatorSelectMenuComponent {
  public readonly customModeCreatorId = model<string | TNil>(null);
  public readonly campaignCreatorsValue = input.required<FormGroup<ICampaignCreatorForm>['value'][]>();
  public readonly ctaForm = input.required<FormGroup<ICtaForm> | TNil>();
  public readonly initialCreators = input.required<ICreator[]>();

  public readonly templateSelectedCreatorIds = input<Set<string> | null>(null);
  public readonly selectedPlatform = input<EPlatform | null>(null);

  public readonly selectedCreatorIdsChange = output<Set<string>>();

  public readonly selectedCreatorIds = signal<Set<string>>(new Set<string>());
  public readonly disabledCreatorIds = signal<Set<string>>(new Set<string>());
  public readonly creatorHasCustomValueMap = signal<Map<string, boolean>>(new Map());

  public readonly globalCreatorSelected = computed(() => {
    return this.selectedCreatorIds().has(ALL_CREATORS_ID);
  });
  public readonly creatorOptions = computed(() => {
    return [
      ALL_CREATORS_OPTION(this.translate),
      ...(this.campaignCreatorsValue()?.reduce((acc, cc) => {
        if (cc.creator) {
          acc.push(cc.creator);
        }

        return acc;
      }, [] as ICreator[]) ?? []),
    ];
  });
  public readonly platformSortedCreatorOptions: Signal<ICreator[]> = computed<ICreator[]>(() => {
    const creatorOptions = this.creatorOptions();
    const disabledIds = this.disabledCreatorIds();

    const sortedCreators: ICreator[] = [...creatorOptions].sort((a: ICreator, b: ICreator) => {
      if (a.id === ALL_CREATORS_ID) {
        return -1;
      }

      if (b.id === ALL_CREATORS_ID) {
        return 1;
      }

      const aDisabled = disabledIds.has(a.id);
      const bDisabled = disabledIds.has(b.id);

      if (aDisabled !== bDisabled) {
        return aDisabled ? 1 : -1;
      }

      return a.name.localeCompare(b.name);
    });

    return uniqBy(sortedCreators, 'id');
  });

  public readonly selectedCreator = computed(() => {
    if (this.selectedCreatorIds().size === 1) {
      return (
        this.creatorOptions().find((creator) => creator.id === this.selectedCreatorIds().values().next().value) ??
        this.initialCreators()[0]
      );
    }

    return this.initialCreators()[0];
  });

  public readonly ALL_CREATORS_ID = ALL_CREATORS_ID;

  private initialCreatorsUsed = false;

  private readonly creatorCustomValueCheckService = inject(CreatorCustomValueCheckService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.setInitialSelectedCreatorsIds();
    this.createDisabledCreatorIdsEffect();
    this.createTemplateSelectedCreatorIdsEffect();
  }

  public onCreatorSelect(event: Event | undefined, creator: ICreator): void {
    event?.stopImmediatePropagation();
    const ctaForm = this.ctaForm();

    if (this.selectedCreatorIds().has(creator.id) && this.selectedCreatorIds().size !== 1) {
      const allCreatorsAlreadySelected = this.selectedCreatorIds().has(ALL_CREATORS_ID);
      const allCreatorsBeingRemoved = allCreatorsAlreadySelected && creator.id === ALL_CREATORS_ID;
      const hasTwoCreatorOptions = this.creatorOptions().length === 2;
      const unselectedCreatorHasCustomMode = this.customModeCreatorId() === creator.id;

      if ((hasTwoCreatorOptions && !allCreatorsBeingRemoved) || (ctaForm?.invalid && unselectedCreatorHasCustomMode)) {
        return;
      }

      ctaForm?.controls?.isAllCreators?.setValue(false);

      SignalSetUtilitiesService.updateSetItems(this.selectedCreatorIds, [], [creator.id, ALL_CREATORS_ID]);

      if (this.customModeCreatorId() === creator.id) {
        this.customModeCreatorId.set(null);
      }
    } else {
      const allCreatorsSelected = creator.id === ALL_CREATORS_ID;
      const nonDisabledCreatorIds = allCreatorsSelected
        ? this.creatorOptions().reduce((acc, c) => {
            if (!this.disabledCreatorIds().has(c.id)) {
              acc.push(c.id);
            }

            return acc;
          }, [] as string[])
        : [];

      SignalSetUtilitiesService.updateSetItems(this.selectedCreatorIds, [creator.id, ...nonDisabledCreatorIds]);

      if (ctaForm && allCreatorsSelected) {
        ctaForm.controls.isAllCreators.setValue(true);
      }
    }

    this.selectedCreatorIdsChange.emit(this.selectedCreatorIds());
  }

  public onCreatorCustomModeChange(event: Event, creator: ICreator): void {
    event?.stopImmediatePropagation();

    if (this.ctaForm()?.invalid || creator.id === ALL_CREATORS_ID) {
      return;
    }

    if (this.customModeCreatorId() === creator.id) {
      this.customModeCreatorId.set(null);
    } else {
      this.customModeCreatorId.set(null);

      requestAnimationFrame(() => {
        this.customModeCreatorId.set(creator.id);
      });
    }
  }

  public calculateCreatorHasCustomValueMap(): void {
    this.creatorHasCustomValueMap.set(
      this.creatorCustomValueCheckService.calculateCreatorsCustomValues(
        this.creatorOptions().map((creator) => creator.id),
        this.ctaForm()?.getRawValue() ?? undefined,
      ),
    );
  }

  private setInitialSelectedCreatorsIds(): void {
    effect(() => {
      if (this.initialCreatorsUsed) {
        return;
      }

      this.initialCreatorsUsed = true;

      if (this.initialCreators().some((creator) => creator.id === ALL_CREATORS_ID)) {
        this.onCreatorSelect(undefined, this.initialCreators()[0]);
      } else {
        this.selectedCreatorIds.set(new Set(this.initialCreators().map((creator) => creator.id)));
      }
    });
  }

  private createDisabledCreatorIdsEffect(): void {
    effect(() => {
      const selectedPlatform = this.selectedPlatform();
      const disabledCreatorIds = new Set<string>();
      const previousSelectedCreatorIds = untracked(() => new Set(this.selectedCreatorIds()));

      if (selectedPlatform) {
        this.creatorOptions().forEach((creator) => {
          if (creator.id === ALL_CREATORS_ID) {
            return;
          }

          if (
            !creator.channels?.some((channel) =>
              SocialUtilitiesService.arePlatformsEqual(channel.platform, selectedPlatform),
            )
          ) {
            disabledCreatorIds.add(creator.id);
          }
        });
      }

      if (previousSelectedCreatorIds.has(ALL_CREATORS_ID)) {
        const compatibleCreatorIds: string[] = this.creatorOptions().reduce(
          (accumulator: string[], creator: ICreator) => {
            if (!disabledCreatorIds.has(creator.id)) {
              accumulator.push(creator.id);
            }

            return accumulator;
          },
          [],
        );

        SignalSetUtilitiesService.updateSetItems(this.selectedCreatorIds, [], [], new Set(compatibleCreatorIds));
      } else {
        SignalSetUtilitiesService.updateSetItems(this.selectedCreatorIds, [], Array.from(disabledCreatorIds));
      }

      this.disabledCreatorIds.set(disabledCreatorIds);
      this.emitSelectedCreatorIdsIfChanged(previousSelectedCreatorIds);
    });
  }

  private createTemplateSelectedCreatorIdsEffect(): void {
    effect(() => {
      if (this.templateSelectedCreatorIds()) {
        this.selectedCreatorIds.set(this.templateSelectedCreatorIds()!);
        this.selectedCreatorIdsChange.emit(this.templateSelectedCreatorIds()!);
      }
    });
  }

  private emitSelectedCreatorIdsIfChanged(previousSelectedCreatorIds: Set<string>): void {
    const currentSelectedCreatorIds = untracked(() => this.selectedCreatorIds());

    if (this.areSetsEqual(previousSelectedCreatorIds, currentSelectedCreatorIds)) {
      return;
    }

    this.selectedCreatorIdsChange.emit(currentSelectedCreatorIds);
  }

  private areSetsEqual(firstSet: Set<string>, secondSet: Set<string>): boolean {
    if (firstSet.size !== secondSet.size) {
      return false;
    }

    return Array.from(firstSet).every((value) => secondSet.has(value));
  }
}
